import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Split } from 'lucide-react'
import { FilterPill } from './FilterPill'
import { FilterPropertyPicker } from './FilterPropertyPicker'
import { useQuickFilters } from './useQuickFilters'
import { useFilterLabels } from './useFilterLabels'
import { FilterScopeContext } from './useFilterValueCounts'
import type { FilterTargetGroup } from '@/store/ProjectFilterStore'
import type { SavedFilterDocument } from '@/lib/db/rxdb/hooks'
import type {
  FilterFieldKey,
  FilterOperator,
  FilterValue,
  TaskFilter,
} from '@/lib/types/filter'
import type { ProjectViewPreferences } from '@/store/ProjectViewPreferencesStore'
import { Button } from '@/components/ui/button'
import useProjectFilterStore from '@/store/ProjectFilterStore'
import useProjectViewPreferencesStore from '@/store/ProjectViewPreferencesStore'
import { FILTER_FIELD_DEFINITIONS } from '@/lib/filters'
import { groupNumbers, isGrouped } from '@/lib/filters/filterGroups'
import { EMPTY_FILTERS } from '@/lib/types/filter'
import { isSavedViewDirty } from '@/lib/types/savedFilter'
import { useProjects } from '@/lib/db/rxdb/hooks'
import useActiveFilterTargetStore from '@/store/ActiveFilterTargetStore'
import { SavedViewActions } from '@/components/features/saved-filters/SavedViewActions'
import { ViewIcon } from '@/components/features/saved-filters/viewIcons'
import { cn } from '@/lib/utils'

interface FilterBarProps {
  projectId: string
  /** Field keys that cannot be removed by the user (e.g. 'project' on Space pages). */
  pinnedFields?: Array<FilterFieldKey>
  /** Field keys that should not appear in the Add-filter property picker. */
  hiddenFields?: Array<FilterFieldKey>
  /** Save-view scope — 'project' (default) or 'space'. */
  scope?: 'project' | 'space'
  /**
   * When this bar is rendered inside a saved view route, pass the hydrated
   * saved-filter document so the bar can offer Update / Save-as-new instead
   * of the generic Save-as-view button.
   */
  currentView?: SavedFilterDocument | null
  /**
   * `row` (default): the strip under the tabs, hidden on phones outside a
   * saved view. `sheet`: the same pills inside the phone Filter sheet
   * (design 36c), shown even when empty so "Add filter" is always there.
   */
  variant?: 'row' | 'sheet'
}

/**
 * Filter pills row — renders below the tabs when filters are active.
 * Returns null when no filters exist.
 */
export function FilterBar({
  projectId,
  pinnedFields,
  hiddenFields,
  scope = 'project',
  currentView,
  variant = 'row',
}: FilterBarProps) {
  const isSheet = variant === 'sheet'
  const { t } = useTranslation('tasks')
  const pinnedSet = useMemo(() => new Set(pinnedFields ?? []), [pinnedFields])
  const filters = useProjectFilterStore(
    (s) => s.filters[projectId] ?? EMPTY_FILTERS,
  )
  const addFilter = useProjectFilterStore((s) => s.addFilter)
  const setOperator = useProjectFilterStore((s) => s.setOperator)
  const toggleValue = useProjectFilterStore((s) => s.toggleValue)
  const setValues = useProjectFilterStore((s) => s.setValues)
  const removeFilter = useProjectFilterStore((s) => s.removeFilter)
  const clearFilters = useProjectFilterStore((s) => s.clearFilters)
  const pendingGroup = useProjectFilterStore((s) => !!s.pendingGroup[projectId])
  const setPendingGroup = useProjectFilterStore((s) => s.setPendingGroup)

  const { projects } = useProjects({ isDeleted: false })
  const { valueSummary, describe } = useFilterLabels()

  const scopedProjectIds = useMemo(() => {
    if (scope !== 'space') return undefined
    return projects
      .filter((p) => !p.isDeleted && p.parentProjectId === projectId)
      .map((p) => p.id)
  }, [projectId, projects, scope])

  // The projects this bar filters over, for value-picker counts (design 9b).
  const countScope = useMemo(
    () => new Set([projectId, ...(scopedProjectIds ?? [])]),
    [projectId, scopedProjectIds],
  )

  // Properties typed filters may not set here: hidden or pinned ones.
  // Keyed by value: callers pass inline arrays.
  const lockedKey = [...(hiddenFields ?? []), ...(pinnedFields ?? [])].join(',')
  const lockedFields = useMemo(
    () => (lockedKey ? (lockedKey.split(',') as Array<FilterFieldKey>) : []),
    [lockedKey],
  )

  // Let ⌘K apply typed filters to this view while it's on screen.
  const setFilterTarget = useActiveFilterTargetStore((s) => s.setTarget)
  const clearFilterTarget = useActiveFilterTargetStore((s) => s.clearTarget)
  useEffect(() => {
    setFilterTarget({ projectId, scope, lockedFields })
    return () => clearFilterTarget(projectId)
  }, [projectId, scope, lockedFields, setFilterTarget, clearFilterTarget])

  const currentPrefs = useProjectViewPreferencesStore(
    (s): ProjectViewPreferences | null => s.preferences[projectId] ?? null,
  )

  const isDirty = useMemo(() => {
    if (!currentView) return false
    return isSavedViewDirty(currentView, filters, currentPrefs)
  }, [currentView, filters, currentPrefs])

  const handleSelectField = useCallback(
    (fieldKey: FilterFieldKey, group: FilterTargetGroup) => {
      addFilter(projectId, fieldKey, { group, lockedFields })
    },
    [projectId, addFilter, lockedFields],
  )

  const handleSetOperator = useCallback(
    (filterId: string, operator: FilterOperator) => {
      setOperator(projectId, filterId, operator)
    },
    [projectId, setOperator],
  )

  const handleToggleValue = useCallback(
    (filterId: string, value: FilterValue) => {
      const filter = filters.find((f) => f.id === filterId)
      const fieldDef = filter ? FILTER_FIELD_DEFINITIONS[filter.fieldKey] : undefined
      if (fieldDef?.fieldType === 'text') {
        setValues(projectId, filterId, value ? [value] : [])
      } else {
        toggleValue(projectId, filterId, value)
      }
    },
    [projectId, filters, toggleValue, setValues],
  )

  const handleSetValues = useCallback(
    (filterId: string, values: Array<FilterValue>) => {
      setValues(projectId, filterId, values)
    },
    [projectId, setValues],
  )

  const isPinned = useCallback(
    (f: TaskFilter) => pinnedSet.has(f.fieldKey) && f.group === undefined,
    [pinnedSet],
  )

  const handleRemoveFilter = useCallback(
    (filterId: string) => {
      const filter = filters.find((f) => f.id === filterId)
      if (!filter || isPinned(filter)) return
      removeFilter(projectId, filterId)
    },
    [projectId, filters, removeFilter, isPinned],
  )

  const handleClearFilters = useCallback(() => {
    if (pinnedSet.size === 0) {
      clearFilters(projectId)
      return
    }
    // Remove non-pinned filters and reset pinned filter values
    for (const f of filters) {
      if (!f.id) continue
      if (isPinned(f)) {
        setValues(projectId, f.id, [])
      } else {
        removeFilter(projectId, f.id)
      }
    }
    setPendingGroup(projectId, false)
  }, [projectId, filters, pinnedSet, isPinned, clearFilters, removeFilter, setValues, setPendingGroup])

  // OR groups (design 9b). Groups only appear once a second one is started.
  const grouped = isGrouped(filters)
  const groups = useMemo(
    () => groupNumbers(filters).map((g) => filters.filter((f) => f.group === g)),
    [filters],
  )
  const canCombine =
    !pendingGroup && filters.some((f) => f.values.length > 0 && !isPinned(f))
  const combineWithOr = useCallback(
    () => setPendingGroup(projectId, true),
    [projectId, setPendingGroup],
  )

  // "Reads as: (Assignee is Me and Cycle is Sprint 1) or (Due is Overdue)"
  const readsAs = useMemo(() => {
    if (!grouped) return null
    const parts = groups
      .map((g) => g.filter((f) => f.values.length > 0).map(describe))
      .filter((d) => d.length > 0)
      .map((d) => (d.length > 1 ? `(${d.join(` ${t('filters.groups.and')} `)})` : (d[0] ?? '')))
    return parts.length > 1 ? parts.join(` ${t('filters.groups.or')} `) : null
  }, [grouped, groups, describe, t])

  const renderPill = (filter: TaskFilter) => (
    <FilterPill
      key={filter.id ?? filter.fieldKey}
      filter={filter}
      valueSummary={valueSummary(filter)}
      onSetOperator={handleSetOperator}
      onToggleValue={handleToggleValue}
      onSetValues={handleSetValues}
      onRemove={handleRemoveFilter}
      allowedProjectIds={filter.fieldKey === 'project' ? scopedProjectIds : undefined}
      pinned={isPinned(filter)}
    />
  )

  const renderAddPicker = (group: FilterTargetGroup, extra?: Partial<GroupAddPickerProps>) => (
    <GroupAddPicker
      projectId={projectId}
      scope={scope}
      lockedFields={lockedFields}
      hiddenFields={hiddenFields}
      filters={filters}
      group={group}
      onSelectField={handleSelectField}
      {...extra}
    />
  )

  const orLabel = (
    <span className="px-0.5 text-[11px] font-semibold uppercase tracking-wide text-brand">
      {t('filters.groups.or')}
    </span>
  )

  // Don't render anything if no filters exist — except when hydrating a saved
  // view, where the bar should remain accessible for Update / Save-as-new.
  if (!isSheet && filters.length === 0 && !currentView) return null
  // A space's built-in "project is any of its projects" filter alone isn't
  // something the user applied: no row for it (design 26c). The Filter
  // button's badge still counts it, and the row returns with any real filter.
  if (!isSheet && !currentView && filters.every((f) => pinnedSet.has(f.fieldKey))) return null

  const hasActiveValues = filters.some((f) => f.values.length > 0)
  const hasConfiguredFilters = filters.length > 0

  // Design 9a: everything in the row is 28px (h-7) with an 8px gap; the
  // view's name sits left, Reset / Save to view are pushed to the right.
  const ghostAction = 'h-7 gap-1 px-2 text-xs text-muted-foreground hover:text-foreground'
  // A plain muted text link (Clear); the save controls live in SavedViewActions.
  const textAction =
    'h-7 px-1.5 text-[12.5px] font-normal text-muted-foreground shadow-none hover:bg-transparent hover:text-foreground'

  return (
    <FilterScopeContext.Provider value={countScope}>
      <div
        className={cn(
          'flex flex-wrap items-center gap-2',
          isSheet ? 'px-4 py-3' : 'border-b border-border px-4 py-2 md:px-8',
          // Phones reach filters through the Filter sheet instead.
          !isSheet && !currentView && 'phone:hidden',
        )}
      >
        {currentView && (
          <span className="flex h-7 items-center gap-1.5 rounded-lg bg-secondary px-2.5 text-[12.5px] font-semibold">
            <ViewIcon icon={currentView.icon} />
            <span className="max-w-48 truncate">{currentView.name}</span>
            {isDirty && (
              <span className="rounded-full bg-brand-tint px-1.5 text-[10.5px] font-semibold leading-4 text-brand-tint-foreground">
                {t('filters.edited')}
              </span>
            )}
          </span>
        )}

        {grouped ? (
          <>
            {/* Pinned filters apply to every group. */}
            {filters.filter((f) => f.group === undefined).map(renderPill)}
            {groups.map((group, i) => (
              <div key={group[0]?.group ?? i} className="flex items-center gap-2">
                {i > 0 && orLabel}
                {/* Outline, not border: the group box mustn't change the row height. */}
                <div className="flex flex-wrap items-center gap-1.5 rounded-lg outline-1 outline-offset-2 outline-border outline-dashed">
                  {group.map((filter, j) => (
                    <div key={filter.id ?? j} className="flex items-center gap-1.5">
                      {j > 0 && (
                        <span className="text-[11px] text-muted-foreground">
                          {t('filters.groups.and')}
                        </span>
                      )}
                      {renderPill(filter)}
                    </div>
                  ))}
                  {renderAddPicker(group[0]?.group)}
                </div>
              </div>
            ))}
          </>
        ) : (
          <>
            {filters.map(renderPill)}
            {renderAddPicker(undefined, {
              onCombineWithOr: canCombine ? combineWithOr : undefined,
              triggerLabel: isSheet ? t('filters.menu.add') : undefined,
            })}
          </>
        )}

        {/* A just-started OR group: its menu opens at once (design 9b). */}
        {pendingGroup && (
          <div className="flex items-center gap-2">
            {orLabel}
            <div className="rounded-lg outline-1 outline-offset-2 outline-brand/50 outline-dashed">
              {renderAddPicker('new', {
                triggerLabel: t('filters.menu.add'),
                autoOpen: true,
                onDismiss: () => setPendingGroup(projectId, false),
              })}
            </div>
          </div>
        )}

        {canCombine && (
          <Button variant="ghost" size="sm" className={ghostAction} onClick={combineWithOr}>
            <Split className="size-3.5" />
            {t('filters.groups.combine')}
          </Button>
        )}

        {/* Actions, right-aligned (design 9a). */}
        <div className={cn('flex items-center gap-2', isSheet ? 'basis-full justify-end border-t pt-3' : 'ml-auto')}>
          {(!currentView || !isDirty) && hasActiveValues && (
            <Button variant="ghost" size="sm" className={textAction} onClick={handleClearFilters}>
              {t('filters.clearAll')}
            </Button>
          )}
          <SavedViewActions
            projectId={projectId}
            scope={scope}
            filters={filters}
            currentPrefs={currentPrefs}
            currentView={currentView}
            isDirty={isDirty}
            hasConfiguredFilters={hasConfiguredFilters}
            pinnedFields={pinnedFields}
          />
        </div>

        {readsAs && (
          <p className="-mt-1 basis-full text-[11px] text-muted-foreground">
            {t('filters.groups.readsAs', { sentence: readsAs })}
          </p>
        )}
      </div>
    </FilterScopeContext.Provider>
  )
}

interface GroupAddPickerProps {
  projectId: string
  scope: 'project' | 'space'
  lockedFields: Array<FilterFieldKey>
  hiddenFields?: Array<FilterFieldKey>
  filters: Array<TaskFilter>
  group: FilterTargetGroup
  onSelectField: (fieldKey: FilterFieldKey, group: FilterTargetGroup) => void
  onCombineWithOr?: () => void
  triggerLabel?: string
  autoOpen?: boolean
  onDismiss?: () => void
}

/** The "+" for one OR group, with quick filters that land in that group. */
function GroupAddPicker({
  projectId,
  scope,
  lockedFields,
  hiddenFields,
  filters,
  group,
  onSelectField,
  ...rest
}: GroupAddPickerProps) {
  const { quickFilters, toggleQuickFilter } = useQuickFilters(projectId, scope, {
    group,
    lockedFields,
  })
  return (
    <FilterPropertyPicker
      projectId={projectId}
      scope={scope}
      lockedFields={lockedFields}
      activeFilters={filters}
      onSelectField={(fieldKey) => onSelectField(fieldKey, group)}
      hasActiveFilters={true}
      hiddenFields={hiddenFields}
      quickFilters={quickFilters}
      onQuickFilter={toggleQuickFilter}
      targetGroup={group}
      {...rest}
    />
  )
}
